import React, { useEffect, useState } from "react";
import Icon from "../../../../components/AppIcon";
import Input from "../../../../components/ui/Input";
import Select from "../../../../components/ui/Select";
import Button from "../../../../components/ui/Button";
import ProfilePhotoUpload from "./ProfilePhotoUpload";
import api from "../../../../api/axiosInstance";

const PersonalInfoSection = () => {

  const [isEditing, setIsEditing] = useState(false);

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    currentRole: "",
    experienceLevel: "",
    targetPosition: "",
    profilePhoto: "",
  });

  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});

  const experienceLevels = [
    { value: "entry", label: "Entry Level (0-2 years)" },
    { value: "mid", label: "Mid Level (3-5 years)" },
    { value: "senior", label: "Senior Level (6-10 years)" },
    { value: "lead", label: "Lead/Principal (10+ years)" },
  ];

  /* ---------- FETCH PROFILE ---------- */

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await api.get("/api/user/profile");

        const data = res.data;

        setFormData({
          fullName: data.name || "",
          email: data.email || "",
          phone: data.phone || "",
          currentRole: data.currentRole || "",
          experienceLevel: data.experienceLevel || "",
          targetPosition: data.targetPosition || "",
          profilePhoto: data.profilePhoto || "",
        });

        setLoading(false);

      } catch (error) {
        console.error("Error fetching profile:", error);
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  if (loading) {
    return <div className="p-6">Loading profile...</div>;
  }

  /* ---------- HANDLERS ---------- */

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null
      }));
    }
  };

  /* ---------- VALIDATION ---------- */

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName?.trim()) {
      newErrors.fullName = "Name is required";
    }

    if (!formData.phone?.trim()) {
      newErrors.phone = "Phone is required";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  /* ---------- SAVE PROFILE ---------- */

  const handleSave = async () => {

    if (!validateForm()) return;

    try {

      await api.put("/api/user/profile", {
        phone: formData.phone,
        currentRole: formData.currentRole,
        experienceLevel: formData.experienceLevel,
        targetPosition: formData.targetPosition,
        profilePhoto: formData.profilePhoto,
      });

      setIsEditing(false);

      alert("Profile updated successfully");

    } catch (error) {

      console.error("Error updating profile:", error);
      alert("Failed to update profile");

    }
  };

  return (
    <div className="bg-card border border-border rounded-lg">

      {/* Header */}

      <div className="px-6 py-4 border-b flex justify-between">

        <div className="flex gap-3">

          <Icon name="User" size={20} />

          <div>
            <h2 className="font-semibold">Personal Information</h2>
            <p className="text-sm text-muted-foreground">
              Manage your profile details
            </p>
          </div>

        </div>

        {!isEditing && (
          <Button size="sm" onClick={() => setIsEditing(true)}>
            Edit
          </Button>
        )}

      </div>

      {/* Body */}

      <div className="p-6 space-y-4">

        <ProfilePhotoUpload
          currentPhoto={formData.profilePhoto}
          onPhotoChange={(photo) => handleChange("profilePhoto", photo)}
        />

        <Input
          label="Full Name"
          value={formData.fullName}
          disabled
        />

        <Input
          label="Email"
          value={formData.email}
          disabled
        />

        <Input
          label="Phone"
          value={formData.phone}
          onChange={(e) => handleChange("phone", e.target.value)}
          error={errors.phone}
          disabled={!isEditing}
        />

        <Input
          label="Current Role"
          value={formData.currentRole}
          onChange={(e) => handleChange("currentRole", e.target.value)}
          disabled={!isEditing}
        />

        <Select
          label="Experience Level"
          value={formData.experienceLevel}
          options={experienceLevels}
          onChange={(value) => handleChange("experienceLevel", value)}
          disabled={!isEditing}
        />

        <Input
          label="Target Position"
          value={formData.targetPosition}
          onChange={(e) => handleChange("targetPosition", e.target.value)}
          disabled={!isEditing}
        />

        {isEditing && (
          <div className="flex gap-3">

            <Button onClick={handleSave}>
              Save
            </Button>

            <Button
              variant="outline"
              onClick={() => setIsEditing(false)}
            >
              Cancel
            </Button>

          </div>
        )}

      </div>

    </div>
  );
};

export default PersonalInfoSection;